import { useEffect } from 'react';
import { productData } from '../data/mockData';
import { buildQuoteDraftUrl } from '../lib/quoteDraftUrl';
import { log } from '../lib/logger';

const DRAFT_STORAGE_KEY = 'egg_draft_quote';

/**
 * Mirrors the current Quote Calculator wizard state into sessionStorage
 * so the global TopNav and FloatingQuoteSummary can render a "Resume
 * draft" affordance + live totals on every page of the site.
 *
 * Cleared automatically when the customer's product/format selection
 * is reset — so navigating "Start over" leaves a clean slate.
 *
 * Side-effect-only hook (returns nothing). Failures (private-browsing
 * quota / disabled storage) are non-fatal and just skip persistence.
 *
 * iter114 — currency-correctness:
 *   The wizard's `total` value is computed in the BASE currency (USD,
 *   rate 1.0). The wizard renders it via `formatCurrency()` which
 *   multiplies by `currencyRates[currency]` for display. We persist
 *   the **already-converted** total here so any consumer that just
 *   reads the blob (FloatingQuoteSummary, TopNav badge, etc.)
 *   displays the same dollar figure the customer saw in the wizard.
 *   Previously we persisted the raw base total, which caused a CAD
 *   user to see `CAD $34.31` in the floating panel vs. `CAD $47.00`
 *   in the wizard — the rate-multiplied value never reached the
 *   floating consumer.
 */
export const useDraftQuotePersistence = ({
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  width,
  height,
  unit,
  quantity,
  shippingMethod,
  total,
  currency,
}) => {
  useEffect(() => {
    if (!selectedProduct || !selectedFormat) {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      return;
    }
    const category = productData.categories.find((c) => c.id === selectedProduct);
    const subcategory = category?.subcategories.find((s) => s.id === selectedFormat);
    const materials = subcategory?.materials || [];
    const material = materials.find((m) => m.id === selectedMaterial);

    // Convert the base total into the customer's chosen currency so
    // every downstream consumer (FloatingQuoteSummary etc.) renders
    // the EXACT figure the wizard displayed. Falls back to 1.0 if
    // the rate map is missing the requested currency.
    const rate = productData.currencyRates?.[currency] ?? 1.0;
    const baseTotal = total ? parseFloat(total) : 0;
    const convertedTotal = baseTotal * rate;

    const draft = {
      productName: subcategory?.name || category?.name || 'Draft Quote',
      materialName: material?.name || null,
      size: width && height ? `${width}×${height} ${unit}` : null,
      quantity: quantity || null,
      totalPrice: convertedTotal,
      currency,
      label: `${selectedProduct}/${selectedFormat}`,
      url: buildQuoteDraftUrl({
        selectedProduct,
        selectedFormat,
        selectedMaterial,
        width,
        height,
        unit,
        quantity,
        shippingMethod,
      }),
      updatedAt: Date.now(),
    };
    try {
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
      // Non-fatal: quota exceeded in private-browsing mode, or storage
      // disabled by the user. Draft simply won't persist across reload.
      log.warn('Failed to persist draft quote to sessionStorage:', err);
    }
  }, [
    selectedProduct,
    selectedFormat,
    selectedMaterial,
    width,
    height,
    unit,
    quantity,
    shippingMethod,
    total,
    currency,
  ]);
};

export default useDraftQuotePersistence;
